/**
 * MemoRevisionPreview - 履歴の版をメモ画面の本文の位置に読み取り専用で出す。上部の帯から戻す・閉じる
 */

import type { JSONContent } from '@tiptap/react'
import { History } from 'lucide-react'
import { useMemo, useState } from 'react'
import { toast } from 'sonner'

import { restoreTextDocumentRevision, type TextDocumentRevision } from '@/api/services/memo'
import { TocProvider } from '@/components/tiptap-node/toc-node/context/toc-context'
import { EditorProvider } from '@/components/tiptap-templates/notion-like/notion-like-editor'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import { confirmDialog } from '@/lib/confirm-dialog'
import { formatRevisionSavedAt, REVISION_REASON_LABEL } from '@/lib/memo-revision'

const EMPTY_DOC: JSONContent = { type: 'doc', content: [] }

type MemoRevisionPreviewProps = {
  /** TextDocument の種別（既定はメモ） */
  entityType?: string
  entityId: number
  revision: TextDocumentRevision
  onClose: () => void
  /** 履歴の内容に戻した後に呼ぶ（エディタを読み込み直す） */
  onRestored: () => void
}

export const MemoRevisionPreview = ({
  entityType = 'memo',
  entityId,
  revision,
  onClose,
  onRestored,
}: MemoRevisionPreviewProps) => {
  const [restoring, setRestoring] = useState(false)
  const savedAt = formatRevisionSavedAt(revision)

  const content = useMemo(() => {
    if (!revision.content_json) return EMPTY_DOC
    try {
      return JSON.parse(revision.content_json) as JSONContent
    } catch {
      return EMPTY_DOC
    }
  }, [revision.content_json])

  const handleRestore = async () => {
    const ok = await confirmDialog({
      title: `${savedAt} の内容に戻しますか？`,
      description: '今の本文は履歴に残るので、あとから戻せます。',
      confirmLabel: '戻す',
    })
    if (!ok) return

    setRestoring(true)
    try {
      await restoreTextDocumentRevision(entityType, entityId, revision.id)
      toast.success('履歴の内容に戻しました')
      onRestored()
    } catch {
      toast.error('戻せませんでした')
    } finally {
      setRestoring(false)
    }
  }

  return (
    <>
      <div className="sticky top-0 z-10 mx-2 mb-2 flex flex-wrap items-center gap-2 rounded-md border border-amber-500/40 bg-amber-500/15 px-3 py-2 backdrop-blur sm:mx-0">
        <History className="size-4 shrink-0 text-amber-500" />
        <span className="min-w-0 flex-1 text-sm">
          <span className="font-medium">{savedAt}</span> の版を表示中
          <span className="ml-1 text-xs text-muted-foreground">（{REVISION_REASON_LABEL[revision.reason]}）</span>
        </span>
        <div className="flex shrink-0 gap-2">
          <Button size="sm" onClick={() => void handleRestore()} disabled={restoring}>
            {restoring ? <Spinner className="size-4" /> : 'この内容に戻す'}
          </Button>
          <Button size="sm" variant="outline" onClick={onClose} disabled={restoring}>
            閉じる
          </Button>
        </div>
      </div>
      <TocProvider>
        <EditorProvider key={revision.id} content={content} editable={false} placeholder="（本文なし）" />
      </TocProvider>
    </>
  )
}
